
import get from 'lodash/get';
import DeleteModal from './DeleteModal.vue';

export default {
  components: {
    DeleteModal,
  },

  props: {
    showModal: {
      type: Boolean,
      required: true,
      default: false,
    },
    isEditing: {
      type: Boolean,
      required: false,
      default: false,
    },
    isDuplicating: {
      type: Boolean,
      required: false,
      default: false,
    },
    wishlistId: {
      type: Number,
      required: false,
      default: null,
    },
    // Input props
    name: {
      type: String,
      required: false,
      default: '',
    },
    description: {
      type: String,
      required: false,
      default: '',
    },
    isPrivate: {
      type: Boolean,
      required: false,
      default: false,
    },
    keepBodyModalClass: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // For internal use, this should be sync with the showModal property
      showModalInternal: this.showModal,
      showDeleteModal: false,
      nameInternal: this.name,
      descriptionInternal: this.description,
      isPrivateInternal: this.isPrivate,
    };
  },

  computed: {
    modalTitle() {
      if (this.isEditing) {
        return 'Edit wishlist';
      }

      if (this.isDuplicating) {
        return 'Duplicate wishlist';
      }

      return 'Create wishlist';
    },

    userId() {
      return get(this, '$store.state.session.user.id');
    },

    requestErrorMessage() {
      return this.$store.getters['wishlists/errorMessage'];
    },

    isProcessing() {
      return this.$store.getters['wishlists/isProcessing'];
    },

    formIsValid() {
      return this.nameInternal?.length <= 50 && this.descriptionInternal.length <= 200;
    },
  },

  watch: {
    showModal(newValue) {
      // Sync internal variable
      this.showModalInternal = newValue;
    },
  },

  methods: {
    clean() {
      if (!this.isEditing && !this.isDuplicating) {
        this.nameInternal = '';
        this.descriptionInternal = '';
        this.isPrivateInternal = false;
      }
    },

    close() {
      this.showModalInternal = false;
      this.clean();
      this.$emit('update:showModal', this.showModalInternal);
      this.$emit('onClose', this.showModalInternal);
    },

    getRequestData() {
      return {
        user: this.userId,
        name: this.nameInternal,
        description: this.descriptionInternal,
        is_public: !this.isPrivateInternal,
        is_active: true,
        originalWishlistId: this.wishlistId,
      };
    },

    beginDeleteList() {
      this.close();
      // add wait here?
      this.updateShowDeleteModal(true);
    },

    updateShowDeleteModal(show = false) {
      // if we are showing delete, don't show the details modal
      this.showDeleteModal = show;
      this.showModalInternal = !show;
    },

    async wishlistStore(action, onComplete, dataOverride = null) {
      if (this.isProcessing) {
        return;
      }

      const requestData = dataOverride || this.getRequestData();

      await this.$store.dispatch(action, requestData);

      if (this.requestErrorMessage) {
        this.$toastr.e(this.requestErrorMessage);
        return;
      }

      // call unique complete function
      onComplete();
    },

    onCreateComplete() {
      this.$toastr.s(`Wishlist <strong>${this.nameInternal}</strong> created!`);
      this.clean();
      this.$emit('wishlistAdded');
    },

    async createList() {
      await this.wishlistStore('wishlists/createWishlist', this.onCreateComplete);
    },

    onEditComplete() {
      this.$toastr.s(`Wishlist <strong>${this.nameInternal}</strong> updated!`);
      this.close();
      this.$emit('wishlistUpdated');
    },

    async editList() {
      await this.wishlistStore('wishlists/updateWishlist', this.onEditComplete);
    },

    onDuplicateComplete() {
      const newSlug = this.$store.getters['wishlists/nextSlug'];

      this.$toastr.s(`Wishlist <strong>${this.nameInternal}</strong> duplicated!`);
      this.$emit('wishlistDuplicated');

      // redirect to the new collection
      window.location = `/collections/wishlists/${newSlug}/`;
    },

    async duplicateList() {
      await this.wishlistStore('wishlists/duplicateWishlist', this.onDuplicateComplete);
    },

    onDeleteComplete() {
      this.$toastr.s(`Wishlist <strong>${this.nameInternal}</strong> was deleted!`);
      this.$emit('wishlistDeleted');

      window.location = '/account/wishlists/';
    },

    async deleteWishlist() {
      const reqData = { wishlistId: this.wishlistId };
      await this.wishlistStore('wishlists/deleteWishlist', this.onDeleteComplete, reqData);
    },
  },
};
